import { motion } from "framer-motion";
import { Landmark, Bot, Plane, RadioTower, Boxes, Sparkles } from "lucide-react";
import { MaskText, FadeUp } from "./MaskText";
import { useLocale } from "@/i18n/LocaleContext";

const ICONS = [Landmark, Bot, RadioTower, Plane, Boxes, Sparkles];
const SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-7", "md:col-span-5"];
const NUMBERS = ["2026", "2", "48h", "1:1"];

export const ServicesGrid = () => {
  const { t } = useLocale();

  return (
    <section id="services" className="relative border-t border-white/10 py-24 md:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <FadeUp>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-nowi-tech">{t.services.kicker}</span>
            </FadeUp>
            <MaskText
              lines={t.services.heading}
              className="mt-5 font-playfair text-4xl md:text-6xl leading-[0.95] tracking-[-0.02em]"
            />
          </div>
          <FadeUp delay={0.1}>
            <p className="max-w-sm text-nowi-muted text-sm md:text-base">{t.services.subtitle}</p>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-white/10 border border-white/10">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={i}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 2) * 0.08, ease: [0.76, 0, 0.24, 1] }}
                className={`${SPANS[i]} group relative bg-nowi-bg p-8 md:p-10 min-h-[240px] flex flex-col justify-between hover:bg-nowi-surface transition-colors duration-500`}
              >
                <div className="flex items-start justify-between">
                  <Icon className="w-8 h-8 text-nowi-text group-hover:text-nowi-tech transition-colors duration-300" strokeWidth={1.25} />
                  <span className="font-mono text-[11px] text-nowi-muted">0{i + 1}</span>
                </div>
                <div>
                  <h3 className="font-playfair text-2xl md:text-3xl mb-3">{s.title}</h3>
                  <p className="text-nowi-muted text-sm max-w-md">{s.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <FadeUp delay={0.1}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border-x border-b border-white/10 mt-px">
            {t.services.stats.map((label, i) => (
              <div key={label} className="bg-nowi-bg p-8 md:p-10">
                <div className="font-playfair text-4xl md:text-5xl text-nowi-text mb-2">{NUMBERS[i]}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nowi-muted">{label}</div>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
